import React, { useState } from 'react';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Box, Typography } from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { BundlePositioningView, InstanceInfo, InstancePositions } from '@paragon/production-app-utilities';
import { naturalSortByProperty } from '@paragon/utilities';

import { removeProductionComponentInstancesFromBundle } from '../../../../actions/productionComponentInstances';
import { getDistanceFormatterSelector } from '../../../../selectors/getDistanceFormatterSelector';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { AddAnotherToThisBundle } from './AddAnotherToThisBundle';

interface BundlePositionBuilderProps {
  readonly height: number;
  readonly width: number;
  readonly instanceInfo: InstanceInfo;
  readonly instancePositions: InstancePositions;
  readonly setInstancePositions: (updatedPositions: InstancePositions) => void;
  readonly quantitiesByTrussName: { [trussName: string]: number };
  readonly disabled?: boolean;
}

export function BundlePositionBuilder(props: BundlePositionBuilderProps) {
  const { quantitiesByTrussName, disabled, height, instanceInfo, instancePositions, setInstancePositions, width } =
    props;
  const dispatch = useAppDispatch();

  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const [selectedInstanceGuid, setSelectedInstanceGuid] = useState<string | undefined>(undefined);

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <Typography>
          {naturalSortByProperty(Object.entries(quantitiesByTrussName), ([trussName]) => trussName)
            .map(([trussName, quantity]) => `${trussName}: ${quantity}`)
            .join(', ')}
        </Typography>
      </Box>
      <Box sx={{ mb: 1 }}>
        <BundlePositioningView
          width={width}
          height={height}
          instancePositions={instancePositions}
          instanceInfo={instanceInfo}
          selectedInstanceGuid={selectedInstanceGuid}
          rightAndBottomPaddingAsAFractionOfOverallHeightOrWidth={0.1}
          eventHandlers={{
            onClick: (guid) => setSelectedInstanceGuid(guid === selectedInstanceGuid ? undefined : guid),
            setInstancePositions,
          }}
          distanceFormatter={distanceFormatter}
          showFourFootLine
          disabled={disabled}
        />
      </Box>
      <TooltipIconButton
        enableInteractive
        title="Remove from bundle"
        disabled={selectedInstanceGuid === undefined}
        onClick={() => {
          dispatch(removeProductionComponentInstancesFromBundle([selectedInstanceGuid!]));
          setSelectedInstanceGuid(undefined);
        }}
      >
        <RemoveCircleIcon />
      </TooltipIconButton>
      <AddAnotherToThisBundle instanceGuid={selectedInstanceGuid} />
    </>
  );
}
